import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import AudioRecorder from "@/components/AudioRecorder";
import { ClipboardList, CheckCircle2, Clock, Send, Paperclip, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Pendente", cls: "bg-[#F59E0B]/15 text-[#b26a00]", icon: Clock },
  submitted: { label: "Enviada", cls: "bg-[#60A5FA]/15 text-[#2563eb]", icon: Send },
  reviewed: { label: "Revisada", cls: "bg-[#34D399]/15 text-[#0F766E]", icon: CheckCircle2 },
};

export default function PatientActivities() {
  const [acts, setActs] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [transcript, setTranscript] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/activities").then((r) => setActs(r.data));
  useEffect(() => { load(); }, []);

  const openRespond = (a) => {
    setActive(a); setText(a.response?.text || ""); setTranscript(a.response?.transcript || ""); setFiles([]);
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((p) => [...p, data]);
      toast.success("Anexo adicionado");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/activities/${active.id}/submit`, {
        text: text || null, transcript: transcript || null, file_ids: files.map((f) => f.id),
      });
      toast.success("Atividade enviada ao seu profissional 💙");
      setActive(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F3B4D] flex items-center gap-2">
          <ClipboardList className="text-[#0F766E]" /> Atividades
        </h1>
        <p className="text-sm text-[#94A3B8]">Responda com texto, áudio, imagem ou documento.</p>
      </div>

      <div className="space-y-3">
        {acts.map((a) => {
          const st = STATUS[a.status]; const Icon = st.icon;
          return (
            <Card key={a.id} data-testid={`activity-card-${a.id}`} className="rounded-2xl p-5 border-[#E0F2FE] card-hover">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${st.cls} border-0 gap-1`}><Icon size={12} /> {st.label}</Badge>
                    <Badge variant="secondary" className="capitalize text-[11px]">{a.type}</Badge>
                  </div>
                  <h3 className="font-semibold text-[#1F3B4D] mt-2">{a.title}</h3>
                  <p className="text-sm text-[#94A3B8] mt-1">{a.instructions}</p>
                  {a.review?.feedback && (
                    <div className="mt-3 rounded-xl bg-[#34D399]/10 border border-[#34D399]/30 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#0F766E]">Retorno do profissional</div>
                      <p className="text-sm text-[#1F3B4D] mt-1">{a.review.feedback}</p>
                    </div>
                  )}
                </div>
                {a.status === "pending" ? (
                  <Button onClick={() => openRespond(a)} data-testid={`respond-activity-${a.id}`}
                    className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] shrink-0">Responder</Button>
                ) : (
                  <Button variant="outline" onClick={() => openRespond(a)} className="rounded-full border-[#E0F2FE] shrink-0" size="sm">Ver</Button>
                )}
              </div>
            </Card>
          );
        })}
        {acts.length === 0 && <Card className="rounded-2xl p-8 text-center text-[#94A3B8] border-[#E0F2FE]">Nenhuma atividade por enquanto.</Card>}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[92vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">{active?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">{active?.instructions}</p>

            <AudioRecorder onComplete={({ transcript, file }) => {
              setTranscript((t) => (t ? t + " " : "") + (transcript || ""));
              if (file) setFiles((p) => [...p, file]);
            }} />

            {transcript && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#0F766E]">Transcrição (revise e corrija)</label>
                <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)}
                  data-testid="activity-transcript" className="mt-1 rounded-xl border-[#E0F2FE] min-h-[90px]" />
              </div>
            )}

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#0F766E]">Escrever</label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} disabled={active?.status !== "pending"}
                data-testid="activity-response-text" placeholder="Escreva sua resposta…"
                className="mt-1 rounded-xl border-[#E0F2FE] min-h-[90px]" />
            </div>

            {active?.status === "pending" && (
              <label className="flex items-center gap-2 text-sm text-[#0F766E] cursor-pointer" data-testid="activity-attach-label">
                <Paperclip size={15} /> Anexar imagem, foto ou PDF
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onUpload} />
              </label>
            )}

            <div className="flex flex-wrap gap-2">
              {(active?.response?.file_ids || files.map((f) => f.id)).map((fid) => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#E0F2FE] text-[#0F766E]">
                  <ImageIcon size={13} /> anexo
                </a>
              ))}
            </div>
          </div>
          {active?.status === "pending" && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setActive(null)}>Cancelar</Button>
              <Button onClick={submit} disabled={saving} data-testid="submit-activity-button"
                className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Send size={16} /> Enviar</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
